<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- 1. لوحة التحكم والفلاتر -->
    <div class="card shadow-sm mb-4 border-top-primary no-print">
      <div class="card-body py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h4 class="mb-0 fw-bold text-primary">
            <i class="fas fa-chart-pie ms-2"></i> الإحصاء العام (متقدم - حاضر - غائب)
          </h4>
          <span class="text-muted small">إحصائيات تفصيلية حسب الإدارة ونوعية التعليم</span>
        </div>
        
        <div class="d-flex gap-2 align-items-center flex-wrap">
          
          <!-- فلتر الإدارة -->
          <div class="input-group">
            <span class="input-group-text bg-light fw-bold">الإدارة</span>
            <select v-model="filters.administration_id" @change="fetchStats" class="form-select fw-bold" style="min-width: 150px;">
                <option value="">-- كل المحافظة --</option>
                <option v-for="admin in administrations" :key="admin.id" :value="admin.id">
                {{ admin.name }}
                </option>
            </select>
          </div>

          <!-- فلتر الفصل الدراسي -->
          <div class="input-group">
            <span class="input-group-text bg-light fw-bold">الفصل</span>
            <select v-model="filters.term" @change="fetchStats" class="form-select fw-bold" style="min-width: 180px;">
                <option value="term1">الفصل الدراسي الأول</option>
                <option value="term2">الفصل الدراسي الثاني</option>
                <option value="round2">الدور الثاني</option>
            </select>
          </div>
          
          <button @click="printPage" class="btn btn-dark px-4" :disabled="!stats">
            <i class="fas fa-print ms-2"></i> طباعة
          </button>
        </div>
      </div>
    </div>

    <!-- 2. منطقة التقرير (للطباعة والعرض) -->
    <div id="printMe" v-if="stats">
      
      <!-- ترويسة الطباعة -->
      <div class="text-center d-none d-print-block mb-4 pt-3 border-bottom pb-3">
        <h3 class="fw-bold">بيان إحصائي عام لنتيجة الشهادة الإعدادية</h3>
        <h5 class="text-muted">للعام الدراسي 2025 / 2026 - {{ getTermName() }}</h5>
        <h6 v-if="filters.administration_id" class="mt-2 border p-1 d-inline-block rounded">
            إدارة: {{ getAdminName() }}
        </h6>
      </div>

      <!-- أ. الملخص العام (Big Cards) -->
      <div class="row g-3 mb-4 text-center">
        <div class="col-md-3 col-6">
          <div class="stat-card bg-primary text-white p-3 rounded shadow-sm h-100 border-2 border-dark">
            <h1 class="fw-bold display-5">{{ stats.general.total }}</h1>
            <span class="fs-5 fw-bold">إجمالي المتقدمين</span>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="stat-card bg-success text-white p-3 rounded shadow-sm h-100 border-2 border-dark">
            <h1 class="fw-bold display-5">{{ stats.general.present }}</h1>
            <span class="fs-5 fw-bold">إجمالي الحضور</span>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="stat-card bg-danger text-white p-3 rounded shadow-sm h-100 border-2 border-dark">
            <h1 class="fw-bold display-5">{{ stats.general.absent }}</h1>
            <span class="fs-5 fw-bold">إجمالي الغياب</span>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="stat-card bg-warning text-dark p-3 rounded shadow-sm h-100 border-2 border-dark">
            <h1 class="fw-bold display-5">{{ stats.general.attendance_rate }}%</h1>
            <span class="fs-5 fw-bold">نسبة الحضور</span>
          </div>
        </div>
      </div>

      <!-- ب. الجدول التفصيلي -->
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-dark text-white fw-bold d-print-none">
          <i class="fas fa-list ms-2"></i> بيان تفصيلي بالفئات
        </div>
        <div class="table-responsive">
          <table class="table table-bordered table-striped text-center align-middle mb-0 custom-table">
            <thead class="bg-light">
              <tr>
                <th rowspan="2" style="width: 25%">البيان / الفئة</th>
                <th rowspan="2" style="width: 15%">المتقدم</th>
                <th colspan="2" style="width: 30%">حالة الحضور</th>
                <th rowspan="2" style="width: 15%">نسبة الحضور</th>
                <th rowspan="2" style="width: 15%">نسبة الغياب</th>
              </tr>
              <tr>
                <th class="text-success">حاضر</th>
                <th class="text-danger">غائب</th>
              </tr>
            </thead>
            <tbody>
              <!-- صفوف الفئات (Looping through categories) -->
              <tr v-for="(category, key) in stats.categories" :key="key">
                <td class="fw-bold text-start ps-3 fs-5">{{ category.label }}</td>
                <td class="fw-bold fs-5">{{ category.total }}</td>
                <td class="text-success fw-bold">{{ category.present }}</td>
                <td class="text-danger fw-bold">{{ category.absent }}</td>
                <td>
                  <div class="progress position-relative border border-dark" style="height: 25px;">
                    <div class="progress-bar bg-success" role="progressbar" :style="{width: category.present_percent + '%'}"></div>
                    <span class="position-absolute w-100 text-center text-dark fw-bold" style="top: 2px;">{{ category.present_percent }}%</span>
                  </div>
                </td>
                <td class="text-muted fw-bold">{{ category.absent_percent }}%</td>
              </tr>
            </tbody>
            <tfoot class="table-dark fw-bold fs-5">
              <tr>
                <td>الإجمالي العام</td>
                <td>{{ stats.general.total }}</td>
                <td class="text-success">{{ stats.general.present }}</td>
                <td class="text-danger">{{ stats.general.absent }}</td>
                <td colspan="2" class="text-center text-warning">{{ stats.general.attendance_rate }}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- ج. الرسم البياني (يظهر فقط على الشاشة) -->
      <div class="d-print-none row mt-4" v-if="chartData.datasets.length > 0">
         <div class="col-12">
             <div class="card border-0 shadow-sm">
                 <div class="card-body">
                     <h5 class="card-title text-center text-primary mb-4 fw-bold">تمثيل بياني لنسب الحضور</h5>
                     <GradesChart :chartData="chartData" />
                 </div>
             </div>
         </div>
      </div>

      <!-- تذييل الطباعة -->
      <div class="mt-5 pt-5 d-none d-print-block">
          <div class="d-flex justify-content-between px-5 text-center fw-bold fs-5">
              <div>رئيس لجنة النظام والمراقبة</div>
              <div>مدير عام التعليم العام</div>
              <div>وكيل الوزارة</div>
          </div>
      </div>

    </div>

    <!-- مؤشر التحميل -->
    <div v-if="loading" class="text-center py-5 no-print">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-2 fw-bold text-muted">جاري تجميع البيانات...</p>
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';
import GradesChart from '@/components/GradesChart.vue'; // تأكد من المسار

export default {
  name: "GeneralStatistics",
  components: { GradesChart },
  data() {
    return {
      loading: false,
      
      // الفلاتر
      filters: {
        term: 'term1',
        administration_id: ''
      },
      
      // البيانات
      stats: null,
      chartData: { labels: [], datasets: [] },

      // قائمة الإدارات (عدلها حسب محافظتك)
      administrations: [
          { id: 1, name: 'العريش' },
          { id: 2, name: 'بئر العبد' },
          { id: 3, name: 'الشيخ زويد' },
          { id: 4, name: 'رفح' },
          { id: 5, name: 'الحسنة' },
          { id: 6, name: 'نخل' },
      ]
    };
  },
  mounted() {
    this.fetchStats();
  },
  methods: {
    async fetchStats() {
      this.loading = true;
      this.stats = null; // تصفير القديم
      
      try {
        // استدعاء الباك إند مع الفلاتر
        const res = await api.get('/admin/reports/general-stats', { 
            params: this.filters 
        });
        
        this.stats = res.data;
        this.prepareChart(); // تحديث الشارت

      } catch (e) {
        console.error("Error fetching stats:", e);
      } finally {
        this.loading = false;
      }
    },

    // تجهيز بيانات الشارت (Chart.js)
    prepareChart() {
        if(!this.stats || !this.stats.categories) return;

        // تحويل الكائن إلى مصفوفة للرسم
        const categoriesArray = Object.values(this.stats.categories);
        
        this.chartData = {
            labels: categoriesArray.map(c => c.label),
            datasets: [
                {
                    label: 'نسبة الحضور',
                    backgroundColor: '#198754',
                    data: categoriesArray.map(c => c.present_percent)
                },
                {
                    label: 'نسبة الغياب',
                    backgroundColor: '#dc3545',
                    data: categoriesArray.map(c => c.absent_percent)
                }
            ]
        };
    },

    // مساعدات للعرض
    getTermName() {
        const names = { 'term1': 'الفصل الدراسي الأول', 'term2': 'الفصل الدراسي الثاني', 'round2': 'الدور الثاني' };
        return names[this.filters.term];
    },
    getAdminName() {
        const admin = this.administrations.find(a => a.id == this.filters.administration_id);
        return admin ? admin.name : '';
    },

    printPage() {
      this.$htmlToPaper("printMe");
    }
  }
};
</script>

<style scoped>
.border-top-primary { border-top: 4px solid #0d6efd; }
.stat-card { transition: transform 0.3s; }
.stat-card:hover { transform: translateY(-5px); }

/* تنسيقات الطباعة الصارمة */
@media print {
    .no-print { display: none !important; }
    .d-print-block { display: block !important; }
    
    .card { border: none !important; box-shadow: none !important; }
    
    /* تنسيق الجدول */
    .custom-table th, .custom-table td {
        border: 2px solid #000 !important;
        font-size: 14pt !important;
        padding: 8px !important;
    }
    
    /* إجبار المتصفح على طباعة ألوان الخلفية (مهم جداً) */
    .bg-primary, .bg-success, .bg-danger, .bg-warning, .bg-dark, .progress-bar {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }
    
    /* جعل الكروت العلوية تظهر كإطار أسود في الطباعة */
    .stat-card {
        border: 2px solid #000 !important;
        background-color: #fff !important; /* خلفية بيضاء لتوفير الحبر */
        color: #000 !important;
    }
    
    /* توسيع المحتوى */
    #printMe {
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }
}
</style>